import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Gift, Copy, CheckCircle, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrals/me").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const copy = () => {
    if (!data?.code) return;
    navigator.clipboard.writeText(data.code);
    toast.success("Code copied");
  };
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { await api.post("/referrals/apply", { ref_code: code.trim() }); toast.success("Referral applied"); setCode(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <div className="label-caps">Invite & earn</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Referrals</h1>
        <p className="text-slate-600 mt-1">Share your code. When someone you refer becomes active, you get £10 in credit.</p>
      </div>

      <div className="card-tactile p-6 grid md:grid-cols-3 gap-4">
        <StatCard icon={Gift} label="Your credit" value={`£${data?.credit_balance ?? 0}`} />
        <StatCard icon={Users} label="Referred users" value={data?.referred_count ?? 0} />
        <StatCard icon={CheckCircle} label="Active referrals" value={data?.referred_active ?? 0} />
      </div>

      <div className="card-tactile p-6">
        <div className="label-caps mb-2">Your code</div>
        <div className="flex items-center gap-2">
          <div data-testid="ref-code" className="font-display text-3xl font-extrabold text-slate-900 tracking-wider select-all bg-slate-50 border border-slate-200 rounded-md px-4 py-3 flex-1">
            {data?.code || "—"}
          </div>
          <Button data-testid="ref-copy-btn" onClick={copy} className="bg-blue-600 hover:bg-blue-700 h-14"><Copy size={16} className="mr-1.5"/> Copy</Button>
        </div>
        <div className="text-xs text-slate-500 mt-2">Share this code with friends or businesses — they can enter it after signing up.</div>
      </div>

      <div className="card-tactile p-6">
        <div className="label-caps mb-2">Got a code from a friend?</div>
        <div className="flex items-center gap-2">
          <Input data-testid="ref-apply-input" value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" className="h-12 uppercase tracking-wider font-semibold" />
          <Button data-testid="ref-apply-btn" onClick={apply} disabled={busy || !code.trim()} className="h-12 bg-slate-900 hover:bg-slate-800">Apply</Button>
        </div>
      </div>

      {data?.referred_users?.length > 0 && (
        <div className="card-tactile p-6">
          <div className="label-caps mb-3">Your referrals</div>
          <div className="space-y-2">
            {data.referred_users.map((r) => (
              <div key={r.email} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-md">
                <div><div className="font-semibold text-slate-900">{r.first_name || r.email}</div><div className="text-xs text-slate-500">{r.role} · {r.email}</div></div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: I, label, value }) {
  return (
    <div>
      <I size={20} className="text-blue-600 mb-2" />
      <div className="label-caps">{label}</div>
      <div className="font-display text-2xl font-extrabold text-slate-900">{value}</div>
    </div>
  );
}
